import React from "react";
import DataBlock from "../overview_widgets/DataBlock";
import { useAppSelector } from "../../hooks";
import { getByPath } from "../../features/Sensors/SensorUtils";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import {
  c_speed_over_ground,
  c_latitude,
  c_longitude,
  c_heading,
  c_primary_depth
} from "../../config/uiconfig";

const DEG_TO_RAD = Math.PI / 180;

function degrees_to_radians(deg: number) {
  return deg * DEG_TO_RAD;
}

type Field = {
  label: string;
  path?: string;
  render?: () => React.ReactNode;
};

export default function CordDisplay() {
  const motd = useAppSelector((state) => state.MOTD.motd);
  const waypoint_north = useAppSelector((state) => state.Waypoint.north);
  const waypoint_east = useAppSelector((state) => state.Waypoint.east);
  const measurements = useAppSelector((state) => state.Measurements.measurements);

  const waypointLat = +waypoint_north;
  const waypointLng = +waypoint_east;

  const calculate_eta = () => {
    const speedRaw = getByPath(c_speed_over_ground, measurements)?.value;
    const distance = parseFloat(calculate_distance());

    if (!speedRaw || distance === -999 || isNaN(distance)) return;

    const speed = parseFloat(speedRaw);
    if (isNaN(speed)) return "No speed";

    if (speed < 1.1) return "Speed to low for ETA calculation";

    const timeToGo = distance / speed;
    if (timeToGo < 0.5) return "We are almost there!";

    const now = Date.now();
    const etaTimestamp = now + timeToGo * 3600000;
    const etaDate = new Date(etaTimestamp);

    if (isNaN(etaDate.getTime())) return "ETA calculation failed due to invalid time";

    const hrs = timeToGo | 0;
    const mins = ((timeToGo * 60) % 60 + 0.5) | 0;

    const nowDate = new Date(now);
    const todayStr = nowDate.toISOString().slice(0, 10);
    const etaStrDate = etaDate.toISOString();
    const etaDateStr = etaStrDate.slice(0, 10);
    const timeStr = etaStrDate.slice(11, 19);

    return `${todayStr === etaDateStr ? "" : etaDateStr + " "}${timeStr} (in ${hrs} hours and ${mins} minutes)`;
  };

  const calculate_distance = () => {
    const targetN = +waypoint_north;
    const targetE = +waypoint_east;

    const nRaw = getByPath(c_latitude, measurements)?.value;
    const eRaw = getByPath(c_longitude, measurements)?.value;

    if (nRaw == null || eRaw == null) return "-999";

    const currentN = +nRaw;
    const currentE = +eRaw;

    if (isNaN(targetN) || isNaN(targetE) || isNaN(currentN) || isNaN(currentE)) {
      return "-999";
    }

    const EARTH_RADIUS_NM = 6371000 / 1852;

    const toRad = degrees_to_radians;

    const phi1 = toRad(currentN);
    const phi2 = toRad(targetN);
    const deltaPhi = toRad(targetN - currentN);
    const deltaLambda = toRad(targetE - currentE);

    const sinDP = Math.sin(deltaPhi / 2);
    const sinDL = Math.sin(deltaLambda / 2);

    const a = sinDP * sinDP + Math.cos(phi1) * Math.cos(phi2) * sinDL * sinDL;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = EARTH_RADIUS_NM * c;

    return d.toFixed(2);
  };

  const formatCoordinate = (value: number, type: "lat" | "lng", format: string) => {
    if (isNaN(value)) return "-";

    const abs = Math.abs(value);
    const degrees = Math.floor(abs);
    const minutesFull = (abs - degrees) * 60;
    const minutes = Math.floor(minutesFull);
    const seconds = (minutesFull - minutes) * 60;

    const direction =
      type === "lat"
        ? value >= 0 ? "N" : "S"
        : value >= 0 ? "E" : "W";

    switch (format) {
      case "DMS":
        return `${degrees}° ${minutes}' ${seconds.toFixed(2)}" ${direction}`;
      case "DMM":
        return `${degrees}° ${minutesFull.toFixed(3)}' ${direction}`;
      case "DD":
      default:
        return `${value.toFixed(5)}° ${direction}`;
    }
  };

  const [format, setFormat] = useLocalStorageState<string>("coordinateFormat", "DD");

  const formattedWaypointLat = formatCoordinate(waypointLat, "lat", format);
  const formattedWaypointLng = formatCoordinate(waypointLng, "lng", format);

  const nRaw = getByPath(c_latitude, measurements)?.value;
  const eRaw = getByPath(c_longitude, measurements)?.value;
  const currentLat = nRaw ? +nRaw : NaN;
  const currentLng = eRaw ? +eRaw : NaN;

  const formattedLat = formatCoordinate(currentLat, "lat", format);
  const formattedLng = formatCoordinate(currentLng, "lng", format);

  const Data: Field[] = [
    {
      label: "Position:",
      path: c_latitude,
      render: () => `${formattedLat}, ${formattedLng}`,
    },
    { label: "Speed:", path: c_speed_over_ground },
    { label: "Heading:", path: c_heading },
    { label: "Depth:", path: c_primary_depth },
  ];

  return (
    <div className="cord-display">
      <div className="cord-choice bot-gap">
        <select
          value={format}
          className="border rounded dark:bg-ndgray dark:border-black"
          onChange={(event) => setFormat(event.target.value)}
        >
          <option value="DD">DD.DDDD</option>
          <option value="DMM">DD MM.MMM</option>
          <option value="DMS">DD MM SS.SSS</option>
        </select>
      </div>

      <div className="chord-table">
        <DataBlock DataFields={Data}>
          <tr className="th-readability even:text-black" key={"Waypoint"}>
            <td className="w-1/2 px-2 pr-10 font-bold">Waypoint</td>
            <td className={"px-2 w-1/2 "}>
              {formattedWaypointLat}, {formattedWaypointLng} (Distance: {calculate_distance()} miles)
            </td>
          </tr>

          <tr className="th-readability even:text-black" key={"ETA"}>
            <td className="w-1/2 px-2 pr-10 font-bold">ETA</td>
            <td className={"px-2 w-1/2 "}>{calculate_eta()}</td>
          </tr>

          <tr className="th-readability even:text-black" key={"Message"}>
            <td className="w-1/2 px-2 pr-10 font-bold">Message</td>
            <td className={"px-2 w-1/2 "}>{motd}</td>
          </tr>
        </DataBlock>
      </div>
    </div>
  );
}